import type { ServerToReactMessages } from '@mntn/sockets'
import { useSocket } from '@mntn/sockets/react'
import { useCallback, useEffect, useRef } from 'react'

/**
 * Cross-origin session sync.
 *
 * Bridges the existing `mntn-advertier-change` BroadcastChannel (same-origin,
 * triggers the reload modal) with select-api so an advertiser switch in any
 * origin (eg. premier, select-ui, flight-deck-ui) reaches the other origins
 * for the same user.
 *
 * Mount once per origin:
 *   - select-ui: at app bootstrap in index.tsx
 *   - flight-deck / premier: inside the AccessGate component
 *
 * Flow:
 *   - Host emits `mntn-advertier-change` on switch.
 *   - This component listens for broadcast, calls the consumer-provided
 *     `notifySessionChanged` (`POST /me/session-changed`).
 *   - select-api fans out via socket to the user's other sockets.
 *   - Receiving instances broadcast `mntn-advertier-change` locally so the
 *     reload modal fires.
 */

/**
 * Intentional typo: must match Premier UI, audience-targeting, and other
 * services. See `apps/select-ui/src/utils/broadcasts/advertiser-change.ts`.
 */
const CHANNEL_NAME = 'mntn-advertier-change'
const RECENT_EMIT_WINDOW_MS = 5_000

/**
 * Loop-suppression state is deliberately module-level — a singleton shared by
 * import reference across every `<SessionSync />` mounted in this JS realm —
 * rather than per-React-instance.
 *
 * `<SessionSync />` is documented "mount once per origin", but a host can
 * accidentally mount it more than once (eg. two flight-deck Module Federation
 * gates in the same premier tab). When the window was per-instance, each set
 * only suppressed its own echo: instance A re-broadcast a fan-out that looked
 * brand-new to instance B, B POSTed, the server fanned that back to A as
 * brand-new, A re-broadcast — an unbounded BroadcastChannel/POST ping-pong.
 * Sharing the window across every instance in the realm breaks that cycle even
 * if the "one instance per origin" contract is violated.
 *
 * A browser tab is its own realm, so the window does not span tabs. Both
 * handlers below therefore also write to it — a replay onto the channel is
 * recorded, and a broadcast whose advertiserId is already recorded is never
 * forwarded — which cuts the same cycle between instances in separate tabs.
 */
const recentEmitTimers = new Map<number, ReturnType<typeof setTimeout>>()

const hasRecentEmit = (advertiserId: number) => recentEmitTimers.has(advertiserId)

const markRecentEmit = (advertiserId: number) => {
  const existing = recentEmitTimers.get(advertiserId)
  if (existing) clearTimeout(existing)
  recentEmitTimers.set(
    advertiserId,
    setTimeout(() => {
      recentEmitTimers.delete(advertiserId)
    }, RECENT_EMIT_WINDOW_MS),
  )
}

/**
 * Defense-in-depth circuit breaker: cap `notifySessionChanged` at one call per
 * advertiserId per `RECENT_EMIT_WINDOW_MS`, ignoring bursts. Even if a future
 * regression re-introduces a feedback loop, the client can no longer emit
 * thousands of `POST /me/session-changed` requests — at most one per advertiser
 * per window. Shared module-level state so the limit spans all instances.
 */
const notifyInFlight = new Set<number>()
const lastNotifyAt = new Map<number, number>()

const canNotify = (advertiserId: number) => {
  if (notifyInFlight.has(advertiserId)) return false
  const last = lastNotifyAt.get(advertiserId)
  return last === undefined || Date.now() - last >= RECENT_EMIT_WINDOW_MS
}

export type SessionSyncProps = {
  /**
   * Tells select-api to fan a session-change out to the user's other open
   * origins. Wired to `POST /me/session-changed`.
   */
  notifySessionChanged: (advertiserId: number) => Promise<void> | void
}

export const SessionSync = ({ notifySessionChanged }: SessionSyncProps) => {
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null)

  const notifyRef = useRef(notifySessionChanged)
  notifyRef.current = notifySessionChanged

  const handleSocketMessage = useCallback((msg: ServerToReactMessages) => {
    if (msg.topic !== 'session-changed') return

    /**
     * Loop prevention: select-api fans the session-changed event out to
     * every socket in the user's room — including the socket that just
     * triggered the POST. If this advertiserId is in the shared recent-emit
     * window, it's the echo of a forward from this browsing context (any
     * instance), not news from another tab/origin. Skip it so the originator
     * doesn't show the reload modal for their own switch. Other tabs/origins
     * have their own realm-level window, so they still process the same
     * fan-out as a genuine event.
     */
    if (hasRecentEmit(msg.payload.advertiserId)) return

    const broadcastChannel = broadcastChannelRef.current
    if (!broadcastChannel) return

    /**
     * Record before replaying. A BroadcastChannel never delivers to the
     * object that posted, so this replay is invisible to the window unless
     * written here — and the next fan-out would read as a fresh switch,
     * leaving an instance in another tab to forward it straight back.
     */
    markRecentEmit(msg.payload.advertiserId)
    broadcastChannel.postMessage(msg.payload.advertiserId)
  }, [])

  useSocket({ onMessage: handleSocketMessage })

  useEffect(() => {
    const broadcastChannel = new BroadcastChannel(CHANNEL_NAME)
    broadcastChannelRef.current = broadcastChannel

    const handler = (event: MessageEvent<number>) => {
      const advertiserId = event.data

      /**
       * A recorded advertiserId means this browsing context already handled
       * it — it forwarded the switch, or an instance here replayed a fan-out
       * onto the channel. Forwarding again hands select-api back its own
       * event and restarts the cycle.
       */
      if (hasRecentEmit(advertiserId)) return

      /**
       * Record the advertiserId we're about to forward so the socket
       * handler can recognize and skip the server's echo back to this
       * browsing context. Cleared after a short window so a legitimate
       * future switch back to the same advertiserId is treated as new. The
       * store is realm-level, so a sibling instance's echo is suppressed
       * too. See the matching guard in `handleSocketMessage` above.
       */
      markRecentEmit(advertiserId)

      /**
       * Circuit breaker: skip the POST if one for this advertiserId is
       * already in flight or fired within the window. Bounds the blast
       * radius of any future feedback loop to one request per advertiser
       * per window instead of an unbounded burst.
       */
      if (!canNotify(advertiserId)) return

      notifyInFlight.add(advertiserId)
      lastNotifyAt.set(advertiserId, Date.now())

      /**
       * Catch so a failed cross-origin notify doesn't surface as an
       * unhandled rejection. The local reload modal still fires for this
       * origin via the host's own broadcast; other origins' next focus
       * refetch is the safety net. Logged so observability picks it up.
       */
      void Promise.resolve(notifyRef.current(advertiserId))
        .catch(err => {
          console.error('[session-sync] notifySessionChanged failed', err)
        })
        .finally(() => {
          notifyInFlight.delete(advertiserId)
        })
    }

    broadcastChannel.addEventListener('message', handler)

    return () => {
      broadcastChannel.removeEventListener('message', handler)
      broadcastChannel.close()
      broadcastChannelRef.current = null
    }
  }, [])

  return null
}
